import * as Yup from 'yup';

import { DateTime } from 'luxon';

import Inventory from './Components/Inventory';
import Adjustments from './Components/Adjustments';
import VendorInfo from './Components/VendorInfo';

import { displayLabelVendor } from '../../utils/displayLabels';

import MultiStatusChip from '../../utils/utilities/MultiStatusChip';

import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import UnpublishedRoundedIcon from '@mui/icons-material/UnpublishedRounded';
import TimelapseRoundedIcon from '@mui/icons-material/TimelapseRounded';

import WigBalance from './Components/WigBalance';

import LineItemList from '../Invoices/Components/LineItemList';

const fields = [
  { 
    fieldType: "debounceSearchSolo",
    formFieldType: "select",
    select: [],
    name: "vendor_id",
    label: "Select Vendor",
    url: "vendors",
    fieldName: "vendor_id",
    displayField: "full_name",
    formUpdates: [{name: "vendor_name", field: "full_name"}, {name: "final_balance", field: "balance"}],
    setDisplayField: "vendor_name",
    displayLabel: displayLabelVendor,
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "formField",
    formFieldType: "date",
    name: "date",
    label: "Bill Date",
    type: "date",
    size: {xs: 12, lg: 3},
  },
  {
    fieldType: "formField",
    formFieldType: "date",
    name: "due_date",
    label: "Due Date",
    type: "date",
    size: {xs: 12, lg: 3},
  },
  {
    fieldType: "divider",
    header: "Bill Details",
    name: "bill_details",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <VendorInfo />,
    name: "vendor_info",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <Inventory />,
    name: "inventory",
    size: {xs: 12, lg: 12},
  },
  // {
  //   fieldType: "divider",
  //   header: "Adjustments",
  //   name: "adjustments",
  //   size: {xs: 12, lg: 12},
  // },
  { 
    fieldType: "Component",
    Component: <Adjustments />,
    name: "adjustments",
    size: {xs: 12, lg: 12},
  },
]

const initialValues = {
  vendor_id: "",
  final_balance: 0,
  services: [],
  products: [],
  wigs: [],
  repairs: [],
  adjustments: [],
  apply_credit: true,
  total_amount: 0,
  date: DateTime.now(),
  due_date: DateTime.now(),
  method: '',
  info: '',
  amount: "",
  notes: '',
  card_num: '',
  exp: '',
  cvv: '',
  entry: 'manual',
  processed: false
}

const additionalState = {
  // customer: {},
  vendor: {},
  vendor_name: '',
  new_payments: [],
  applied_payments: [],
  final_balance: 0,
}

const headers = [
  {
    label: 'Bill Number',
    id: 'bill_num'
  },
  {
    label: 'Vendor',
    id: 'vendor_name'
  },
  {
    label: "Items",
    id: "line_items",
    render: row => <LineItemList line_items={row.line_items} />
  },
  {
    label: 'Total',
    id: 'total',
    currency: true
  },
  {
    label: "Status",
    id: "paid_status",
    multi: true,
    values: [{value: "paid", status: "success", label: "Paid"}, {value: "partially_paid", status: "warning", label: "Partially Paid"}, {value: "unpaid", status: "error", label: "Unpaid"}]
  },
  {
    label: "Balance",
    currency: true,
    id: "balance"
  },
  {
    label: "Date",
    id: "date",
    date: true
  }
];

const balance_headers = [
  {
    label: 'Wig ID',
    id: 'wig_id'
  },
  {
    label: "SKU",
    id: "wig_sku"
  },
  {
    label: 'Brand',
    id: 'brand_name'
  },
  {
    label: 'Total Cost',
    id: 'total_cost',
    currency: true
  },
  {
    label: "Paid",
    id: "total_paid",
    currency: true
  },
  {
    label: "Balance",
    currency: true,
    id: "balance_due"
  },
  {
    label: "Sold To",
    id: "customer_name"
  },
  {
    label: "Customer Balance",
    id: "balance",
    render: row => <WigBalance wig={row} />
  },
  {
    label: "Sale Date",
    id: "sold_date",
    dateShort: true
  }
]

const filters = [
  {
    id: "total",
    type: "total",
    label: "Total",
  },
  {
    id: "balance",
    type: "balance",
    label: "Balance",
  },
  {
    id: "date",
    type: "date",
    label: "Date",
  }
]

const tabs = [
  {id: "all", label: "All", Icon: AllInclusiveRoundedIcon },
  {id: "unpaid", label: "Unpaid", Icon: UnpublishedRoundedIcon },
  {id: "partially_paid", label: "Partially Paid", Icon: TimelapseRoundedIcon },
  {id: "paid", label: "Paid", Icon: CheckCircleOutlineRoundedIcon },
]

const balance_filters = [
  {
    id: "brand_name",
    type: "autoMulti",
    label: "Wig Brand",
    url: '/vendors/brands',
    fieldOptions: {
      fieldName: "brand_name",
      displayField: "brand_name"
    },
    prefetch: true
  },
  {
    id: "total_cost",
    type: "number",
    label: "Total Cost",
  },
  {
    id: "total_paid",
    type: "number",
    label: "Paid Vendor",
  },
  {
    id: "balance_due",
    type: "balance",
    label: "Vendor Balance",
  },
  {
    id: "sold_price",
    type: "number",
    label: "Sale Total",
  },
  {
    id: "sold_paid",
    type: "number",
    label: "Paid Customer",
  },
  {
    id: "sold_balance_due",
    type: "balance",
    label: "Customer Balance",
  },
  {
    id: "sold_date",
    type: "date",
    label: "Sold Date",
  }
]

const schema = Yup.object().shape({});

const parseData = (data) => {
  const parsed = {
    ...data,
    method: '',
    info: '',
    amount: "",
    notes: '',
    card_num: '',
    exp: '',
    cvv: '',
    entry: 'manual',
    processed: false,
    apply_credit: false
  };
  parsed.date = DateTime.fromISO(data.date);
  parsed.due_date = DateTime.fromISO(data.due_date);
  parsed.vendor_id = data.vendor_id.toString();
  return parsed
}

export { headers, filters, fields, initialValues, additionalState, schema, parseData, balance_headers, balance_filters, tabs };